/* eslint-disable prefer-promise-reject-errors */
import { useLocales } from '@/Locales';
import { existUser } from '@/services/sys';
import { getVerifyCode, httpSystemSetting } from '@/services/user';
import { userInfoAtom } from '@/store/atom';
import { validIsMobile } from '@/utils';
import { clearSystemUserInfo, removeToken } from '@/utils/cookies';
import { CheckboxChangeEvent } from '@sinohealth/butterfly-ui-antd/lib/checkbox';
import { Button, Checkbox, Form, Input, message } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import styles from '../../index.less';
import ProtocolModal, { ProtocolModalRef } from '../ProtocolModal';

const sendCodeTimeOut = 60;

interface AccountFormProps {
  loading?: boolean;
  onLogin: (params: any) => Promise<void>;
  onRegister?: (params: any) => Promise<void>;
}

const MobileForm: React.FC<AccountFormProps> = ({ onLogin, loading, onRegister }) => {
  const [form] = Form.useForm();
  const [agree, setAgree] = useState(false);
  const [enableMFA, setEnableMFA] = useState(false);
  const [loadingSetting, setLoadingSetting] = useState(false);
  const [phoneNo, setPhoneNo] = useState('');
  const [canSendCode, setCanSendCode] = useState(true);
  const [hasSendCode, setHasSendCode] = useState(false);

  const [timeLeft, setTimeLeft] = useState(0);
  const setUserInfo = useSetRecoilState(userInfoAtom);
  // 表单校验后，所有字段有值且勾选协议才可登录
  const [canLogin, setCanLogin] = useState(false);
  const protocolModalRef = useRef<ProtocolModalRef>(null);
  const existUserFlag = useRef(true);

  const { t } = useLocales();

  /**
   * 处理登录提交
   * @param values 表单数据
   */
  const handleLogin = async (values: any) => {
    try {
      const { phone, captcha, channel } = values;

      // 检查是否同意协议
      if (!agree) {
        // 未同意协议，弹出协议确认弹窗
        protocolModalRef.current?.open({ phone, captcha, channel });
        return;
      }
      console.log({ existUserFlag: existUserFlag.current, phone, captcha, channel });
      if (!existUserFlag.current) {
        // 还未注册，需要调注册
        await onRegister?.({ phone, captcha, channel });
      } else {
        // 调用登录接口
        await onLogin({ phone, captcha, channel });
      }
    } catch (error) {
      console.error('登录处理失败:', error);
    }
  };

  const onFieldsChange = () => {
    const values = form.getFieldsValue();
    const errors = form.getFieldsError();
    setCanLogin(!!values.phone && !!values.captcha && agree);
  };

  const onCheckboxChange = (e: CheckboxChangeEvent) => {
    setAgree(e.target.checked);
    const values = form.getFieldsValue();
    setCanLogin(!!values.phone && !!values.captcha && e.target.checked);
  };

  const handleSendSmsCode = async () => {
    if (!phoneNo) {
      message.error(t('手机号码不能为空'));
      return;
    }

    // 确保手机号格式正确且用户存在（通过Form校验）
    try {
      await form.validateFields(['phone']);
    } catch (error) {
      // 如果校验失败，不发送验证码
      return;
    }

    try {
      await getVerifyCode(phoneNo);
      setHasSendCode(true);
      setTimeLeft(sendCodeTimeOut);
      setCanSendCode(false);
    } catch (error) {
      console.error('发送验证码失败:', error);
      message.error(t('发送验证码失败，请稍后重试'));
    }
  };

  const validPhoneNo = async (rule: any, value: any) => {
    if (!value || value === '') {
      return Promise.reject(t('手机号码不能为空'));
    }
    if (value.length < 11) {
      return Promise.reject(t('请输入11位手机号码'));
    }
    if (value.length >= 11 && !validIsMobile(value)) {
      message.error(t('手机号格式不正确'));
      return Promise.reject(t('手机号格式不正确'));
    }
    return Promise.resolve();
  };

  const validCaptcha = (rule: any, value: any) => {
    if (!value) {
      return Promise.reject(t('验证码不能为空'));
    }
    if (value && value?.length > 4) {
      message.error(t('请输入4位验证码'));
      return Promise.reject(t('请输入4位验证码'));
    }
    return Promise.resolve();
  };

  /**
   * 检查手机号是否存在
   * @param phone 手机号
   */
  const checkPhoneExists = async (phone: string) => {
    try {
      const userExists: any = await existUser({ phone });
      console.log({ userExists });
      existUserFlag.current = userExists;

      // 显示用户反馈
      // if (!userExists) {
      //   message.error('该手机号未进行注册');
      // }

      return userExists;
    } catch (error) {
      console.error('检查用户存在性失败:', error);
      existUserFlag.current = false;

      return false;
    }
  };

  const agreementValid = (rule: any, val: any) => {
    return val ? Promise.resolve() : Promise.reject();
  };

  useEffect(() => {
    clearSystemUserInfo();
    // httpSystemSettingReq();
    removeToken();
    setUserInfo(null); // 进入登录页就清空全局状态的用户信息
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      setCanSendCode(false);
      setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 900);
    } else {
      setCanSendCode(true);
    }
  }, [timeLeft]);

  /** 获取登录配置 */
  const httpSystemSettingReq = async () => {
    try {
      setLoadingSetting(true);
      const res: any = await httpSystemSetting();
      // console.log(res);
      setEnableMFA(res.enableMFA || false);
    } catch (err) {
      console.log(ErrorEvent);
    } finally {
      setLoadingSetting(false);
    }
  };

  return (
    <>
      <Form form={form} className={styles.loginForm} onFieldsChange={onFieldsChange} onFinish={handleLogin}>
        <Form.Item name="channel" hidden noStyle initialValue="phone" />
        <Form.Item name="phone" rules={[{ required: true, validator: validPhoneNo }]}>
          <Input
            className={styles['form-input']}
            size="large"
            maxLength={11}
            placeholder={t('请输入登录手机号')}
            prefix={<span className={classNames(styles['form-icon'], 'iconfont icon-a-3')} />}
            onChange={(e) => {
              const phone = e.target.value;
              setPhoneNo(phone);
              // 手机号格式正确后检查是否存在
              if (validIsMobile(phone)) {
                checkPhoneExists(phone);
              }
            }}
          />
        </Form.Item>
        <Form.Item name="captcha" rules={[{ required: true, validator: validCaptcha }]}>
          <Input
            className={styles['form-input']}
            size="large"
            placeholder={t('请输入登录验证码')}
            prefix={<span className={classNames(styles['form-icon'], 'iconfont icon-a-4')} />}
            suffix={
              <Button
                type="link"
                size="small"
                disabled={!canSendCode || !validIsMobile(phoneNo)}
                onClick={() => handleSendSmsCode()}
              >
                {canSendCode
                  ? t(hasSendCode ? '重新获取验证码' : '获取验证码')
                  : `${timeLeft}s${t('后重新获取')}`}
              </Button>
            }
          />
        </Form.Item>
        <Form.Item
          className={styles.agreeWrap}
          rules={[
            {
              validator: agreementValid,
              message: t('请先阅读勾选《用户服务协议》《隐私政策》'),
            },
          ]}
        >
          <Checkbox checked={agree} onChange={onCheckboxChange}>
            {t('已阅读并同意')}
          </Checkbox>
          <span
            className={styles.protocol}
            onClick={() =>
              window.open(`${window.location.pathname}#/login/user-protocol?id=1`, '_blank')
            }
          >
            {t('《用户服务协议》')}
            {t('和')}
          </span>
          <span
            className={styles.protocol}
            onClick={() =>
              window.open(`${window.location.pathname}#/login/user-protocol?id=2`, '_blank')
            }
          >
            {t('《隐私政策》')}
          </span>
          <div className={styles.protocol}>未注册的手机号将自动注册</div>
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            className={styles.loginBtn}
            loading={loading || loadingSetting}
            size="large"
            htmlType="submit"
          >
            {t('登 录')}
          </Button>
        </Form.Item>
      </Form>
      <ProtocolModal
        ref={protocolModalRef}
        loading={loading}
        onConfirm={(values, flag) => {
          setAgree(flag);
          onLogin(values);
        }}
        onCancel={setAgree}
      />
    </>
  );
};

export default MobileForm;
